import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { AppConfig } from 'src/app/core/configs/app.config';
import { ChannelService } from 'src/app/services/channel.service';
import { MediaPlanService } from 'src/app/services/media-plan.service';
import { environment } from '../../../../environments/environment';
import { CampaignService } from '../../../services/campaign.service';
import {
    showChannelStatusDistinction,
    getChannelSupplierStatusForMedia,
    shouldShowExitConfirmationModal,
    checkIfModeExistsInQueryParams
} from 'src/app/shared/utility/campaign';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { PaymentService } from '../../../services/payment.service';
import { PublishCampaignModalComponent } from '../publish-campaign-modal/publish-campaign-modal.component';
import { ChannelStatus } from 'src/app/models/channel';
import { UserRole } from 'src/app/models/user';
import { CanComponentDeactivate } from '../guards/can-deactivate.interface';

export interface Summary {
    channel: string;
    start_date: string;
    end_date: string;
    country: string;
    city: string;
    price: string;
    currency: string;
}

@Component({
    selector: 'app-campaign-summary',
    templateUrl: './campaign-summary.component.html',
    styleUrls: ['./campaign-summary.component.scss']
})
export class CampaignSummaryComponent implements OnInit, CanComponentDeactivate {
    /** table  **/
    public ELEMENT_DATA: Summary[] = [];

    get displayedColumns(): string[] {
        const columns = [
            'media',
            'channelName',
            'location',
            'startDate',
            'endDate',
            'rule',
            'screens',
            'impressions',
            'plays',
            'cost'
        ];

        return this.campaign?.billingMethod === 'PER_HOUR'
            ? columns.filter(column => column !== 'impressions')
            : columns.filter(column => column !== 'plays');
    }

    displayedColumnsMunicipality: string[] = ['supplierName', 'location', 'cost'];
    dataSource = new MatTableDataSource<Summary>(this.ELEMENT_DATA);
    dataSourceMunicipality = new MatTableDataSource<Summary>(this.ELEMENT_DATA);
    campaignId;
    campaign;
    suppliersIds;
    isSupplier: boolean = false;
    campaignSummary;
    mediaUrl = environment.apiUrl;
    campaignVAT = 0;
    sectionData: any[];
    previewMode = false;
    partialEditMode = false;
    editMode = false;
    liveEditMode = false;
    isPublishing = false;

    selectedFileType: 'excel' | 'pdf' | null = null;
    loading = false;

    constructor(
        public dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute,
        private authenticationService: AuthenticationService,
        private campaignService: CampaignService,
        private channelService: ChannelService,
        private paymentService: PaymentService,
        private campaignProposal: MediaPlanService
    ) { }

    ngOnInit(): void {
        this.previewMode = checkIfModeExistsInQueryParams(this.route, 'preview');
        this.liveEditMode = checkIfModeExistsInQueryParams(this.route, 'live-edit-mode');
        this.partialEditMode = checkIfModeExistsInQueryParams(this.route, 'partial-edit-mode');
        this.editMode = checkIfModeExistsInQueryParams(this.route, 'edit-mode');
        this.getCampaign();
        this.getChannels();
    }

    canDeactivate(): boolean {
        return this.isPublishing || (this.previewMode && !this.editMode);
    }

    async saveDraft(): Promise<boolean> {
        return Promise.resolve(true);
    }

    showChannelStatusDistinction() {
        return showChannelStatusDistinction(
            this.campaign?.status,
            this.authenticationService.getSessionRole()
        );
    }

    getCampaign() {
        this.campaignId = this.route.snapshot.paramMap.get('id');
        if (this.campaignId != null) {
            this.campaignService.getById(this.campaignId).subscribe(campaign => {
                this.campaign = campaign;
                this.getCampaignDetails();
            });
        }
    }

    getChannels() {
        this.campaignId = this.route.snapshot.paramMap.get('id');
        if (this.campaignId != null) {
            this.channelService
                .getChannelsSuppliersIdsByCampaignId(this.campaignId)
                .subscribe(suppliersIds => {
                    this.suppliersIds = suppliersIds;
                });
        }
    }

    handleMediaOverviewSorting(mediaOverviewDTOS) {
        if (this.showChannelStatusDistinction()) {
            const statusOrder = [
                ChannelStatus.APPROVED,
                ChannelStatus.REJECTED,
                ChannelStatus.PENDING_REAPPROVAL,
                ChannelStatus.PENDING_APPROVAL,
                ChannelStatus.DRAFT
            ];
            return mediaOverviewDTOS.sort((a, b) => {
                return (
                    statusOrder.indexOf(getChannelSupplierStatusForMedia(this.campaign, a, true)) -
                    statusOrder.indexOf(getChannelSupplierStatusForMedia(this.campaign, b, true))
                );
            });
        }
        return mediaOverviewDTOS;
    }

    getChannelSupplierStatusForMedia(campaign, media) {
        return getChannelSupplierStatusForMedia(campaign, media, true);
    }

    getCampaignDetails() {
        this.campaignId = this.route.snapshot.paramMap.get('id');
        const activeRole = this.authenticationService.getSessionRole();

        this.campaignService.getCampaignSummary(this.campaignId).subscribe(
            (summary: any) => {
                this.campaignSummary = summary;
                this.isSupplier = activeRole === UserRole.SUPPLIER;

                this.dataSource = new MatTableDataSource(
                    this.handleMediaOverviewSorting(summary.mediaOverviewDTOS)
                );

                this.dataSourceMunicipality = new MatTableDataSource(
                    summary.municipalityFees
                );

                this.sectionData = [
                    {
                        labelColumn1: 'Price',
                        valueColumn1: summary.mediaTotal,
                        labelColumn2: 'Municipality Fees',
                        valueColumn2: summary.municipalityFeesTotal,
                        labelColumn3: 'sub-total',
                        valueColumn3: summary.subTotal
                    },
                    {
                        labelColumn1: 'sub-total',
                        valueColumn1: summary.subTotal,
                        labelColumn2: 'VAT',
                        valueColumn2: {
                            value: summary.vatPercentage,
                            suffix: '%'
                        },
                        labelColumn3: 'total',
                        valueColumn3: summary.grandTotal
                    }
                ];
            }
        );
    }

    // No active channels left (e.g. all were removed after a supplier/channel deactivation) —
    // there is nothing to publish, so the publish button is disabled.
    public hasNoActiveChannels(): boolean {
        return !this.campaign?.channels?.length;
    }

    public publishCampaign() {
        this.isPublishing = true;
        const dialogRef = this.dialog.open(PublishCampaignModalComponent, {
            width: AppConfig.dialog_width_xs,
            maxHeight: AppConfig.dialog_max_height,
            data: {
                previewMode: this.previewMode || this.liveEditMode,
                editMode: this.editMode || this.partialEditMode,
                campaign: this.campaign,
                hasMultipleSuppliers: this.suppliersIds.length > 1
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                this.isPublishing = false;
            }
        });
    }

    onFileTypeSelect(fileType: 'excel' | 'pdf') {
        if (!fileType) {
            return;
        }

        this.loading = true;
        const campaignId = this.campaignId;

        const apiCall =
            fileType === 'pdf'
                ? this.campaignProposal.downloadAsPDF(campaignId)
                : this.campaignProposal.downloadAsExcel(campaignId);

        apiCall.subscribe(
            (response: Blob) => {
                const fileName = `campaign_proposal.${fileType === 'pdf' ? 'pdf' : 'xlsx'}`;
                this.downloadFileFromBlob(response, fileName);
                this.loading = false;
                this.selectedFileType = null;
            },
            error => {
                this.loading = false;
            }
        );
    }

    private downloadFileFromBlob(blob: Blob, fileName: string) {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    }
}
